<template>
    <div class="create-edit-main-container" v-if="!loading">
        <h1 v-if="isCreateRoute">
            {{ $t("monibox.form.add") }}
        </h1>
        <h1 v-else>
            {{ $t("monibox.optionsMenu.generalInfos") }}
        </h1>
        <p>* {{ $t("vigik.form.obligatoryFields") }}</p>
        <div class="content-container">
            <div class="form-container">
                <div class="input-container">
                    <div class="input-wrapper">
                        <p>
                            {{ $t("residencesList.arrayColumns.name") }}
                            <span>*</span>
                        </p>
                        <BasicInput
                            v-model="name"
                            name="moniboxName"
                            maxlength="40"
                            :disabled="!checkRight('G_RESIDENCES')"
                        />
                    </div>
                    <div class="input-wrapper">
                        <p>{{ $t("SERIAL_NUMBER") }} <span>*</span></p>
                        <BasicInput
                            class="input"
                            :maxlength="11"
                            v-model="moniboxSerial"
                            name="moniboxSerial"
                            :disabled="!checkRight('G_RESIDENCES')"
                        >
                            <template #maxLengthCount>
                                {{
                                    moniboxSerial?.replace(/-/g, "").length ?? 0
                                }}
                                /
                                {{ maxLengthMoniboxSerial }}
                            </template>
                        </BasicInput>
                    </div>
                    <div class="input-wrapper">
                        <p>{{ $t("COMMENT") }}</p>
                        <BasicInput
                            v-model="comment"
                            name="moniboxComment"
                            :textarea="true"
                            :disabled="!checkRight('G_RESIDENCES')"
                        />
                    </div>
                </div>
                <div class="buttons-wrapper" v-if="checkRight('G_RESIDENCES')">
                    <BasicButton
                        color="orange"
                        :disabled="
                            !canBeCreateOrUpdate || loadingCreateOrUpdate
                        "
                        :loading="loadingCreateOrUpdate"
                        @click="saveMethods"
                    >
                        {{ $t("button.register") }}
                    </BasicButton>
                    <BasicButton
                        color="blue"
                        v-if="!isCreateRoute"
                        @click="eraseMonibox"
                    >
                        {{ $t("button.delete") }}
                    </BasicButton>
                    <BasicButton
                        color="blue"
                        v-if="!isCreateRoute && residenceType === 0"
                        :disabled="loadingMoveTo"
                        :loading="loadingMoveTo"
                        @click="moveMonibox"
                    >
                        {{ $t("button.moveTo") }}
                    </BasicButton>
                    <BasicButton
                        color="blue"
                        v-if="isCreateRoute"
                        @click="$router.go(-1)"
                    >
                        {{ $t("button.cancel") }}
                    </BasicButton>
                </div>
            </div>
            <div class="separator" v-if="isCreateRoute" />
            <div class="image-wrapper" v-if="isCreateRoute">
                <img
                    :src="require('@/assets/images/icons/monibox.svg')"
                    alt=""
                />
            </div>
        </div>
    </div>
    <div v-else class="loader">
        <Loader />
    </div>
</template>

<script>
import {
    getMonibox,
    createMonibox,
    updateMonibox,
    deleteMonibox,
} from "@/services/intratone/monibox"
import { getCages } from "@/services/intratone/residence"
import { searchDeep, parseStringPath } from "@/helpers/commonFunctions"

import BasicInput from "@/components/basic/BasicInput.vue"
import BasicButton from "@/components/basic/BasicButton.vue"
import Loader from "@/components/basic/Loader.vue"

export default {
    components: {
        BasicInput,
        BasicButton,
        Loader,
    },

    data() {
        return {
            name: null,
            serial: null,
            maxLengthMoniboxSerial: 9,
            comment: null,
            initialData: {},
            loading: false,
            loadingCreateOrUpdate: false,
            loadingMoveTo: false,
        }
    },

    emits: ["loading"],

    computed: {
        isCreateRoute() {
            return this.$route.params.action === "create"
        },

        routeName() {
            return this.$route.params.action === "edit"
                ? this.name
                : this.$t("monibox.form.add")
        },

        moniboxSerial: {
            get() {
                return (
                    this.serial
                        ?.replace(/-/g, "")
                        .toUpperCase()
                        .match(/.{1,3}/g)
                        ?.join("-") ?? this.serial
                )
            },
            set(value) {
                const cleaned = value.replace(/-/g, "")
                this.serial = cleaned.slice(0, 9)
            },
        },

        canBeCreateOrUpdate() {
            return (
                this.name &&
                this.serial &&
                this.serial.replace(/-/g, "").trim().length === 9 &&
                (this.initialData.name !== this.name ||
                    this.initialData.comments !== this.comment ||
                    this.initialData.serial !==
                        this.serial.replace(/-/g, "").trim())
            )
        },

        residenceId() {
            const store = this.$store.getters["breadCrumb/getClassicBreadCrumb"]

            if (this.isCreateRoute) {
                const residence = store.find((item) => {
                    return item.routeName === "Residence"
                })

                return residence.id ? residence.id : null
            } else {
                return this.initialData.idres
            }
        },

        residenceName() {
            const store = this.$store.getters["breadCrumb/getClassicBreadCrumb"]

            const residence = store.find((item) => {
                return item.routeName === "Residence"
            })

            return residence.name ? residence.name : null
        },

        residenceType() {
            const store = this.$store.getters["breadCrumb/getClassicBreadCrumb"]

            const residence = store.find((item) => {
                return item.routeName === "Residence"
            })

            return residence.options?.type
        },

        buildingId() {
            if (this.$route.query.cageId) {
                return this.$route.query.cageId
            } else {
                if (this.isCreateRoute) {
                    const store =
                        this.$store.getters["breadCrumb/getClassicBreadCrumb"]
                    const lastIndex = store[store.length - 2]

                    if (
                        lastIndex.routeName !== "Residences" &&
                        lastIndex.routeName !== "Residence"
                    ) {
                        return lastIndex.id
                    } else {
                        return 0
                    }
                } else {
                    return this.initialData.idCage
                }
            }
        },
    },

    methods: {
        async saveMethods() {
            switch (this.isCreateRoute) {
                case true:
                    await this.declareMonibox()
                    break
                case false:
                    await this.modifyMonibox(this.residenceId, this.buildingId)
                    break
            }
        },

        async declareMonibox() {
            this.loadingCreateOrUpdate = true

            this.serial = await this.serial.replace(/-/g, "")

            const payload = {
                name: this.name,
                serial: this.serial.trim(),
                residence_id: this.residenceId,
                building_id: this.buildingId,
                comments: this.comment,
            }

            await createMonibox(payload)
                .then((response) => {
                    if (!response.error) {
                        this.$router.go(-1)
                    }
                })
                .finally(() => {
                    this.formateSerial()
                    this.loadingCreateOrUpdate = false
                })
        },

        async retrieveMonibox() {
            this.$emit("loading", true)
            await getMonibox(this.$route.params.id).then((response) => {
                this.initialData = response.data
                this.name = response.data.name
                this.serial = response.data.serial
                this.comment = response.data.comments

                this.formateSerial()

                this.$emit("change-monibox-name", this.name)
                this.$emit("loading", false)
            })
        },

        async modifyMonibox(residenceId, buildingId) {
            this.loadingCreateOrUpdate = true

            const payload = {
                name: this.name,
                residence_id: residenceId,
                building_id: buildingId,
                comments: this.comment,
            }

            if (this.serial.replace(/-/g, "") !== this.initialData.serial) {
                this.serial = this.serial.replace(/-/g, "")
                payload.serial = this.serial.trim()
            }

            await updateMonibox(this.$route.params.id, payload)
                .then((response) => {
                    if (!response.error) {
                        this.$router.go(-1)
                    }
                })
                .finally(() => {
                    this.formateSerial()
                    this.loadingCreateOrUpdate = false
                })
        },

        async eraseMonibox() {
            this.openModal("validation", {
                title: "portaconnect.delete",
                question: this.$t("monibox.form.delete"),
                validateButton: this.$t("button.delete"),
                valid: async () => {
                    await deleteMonibox(this.$route.params.id).then(
                        (response) => {
                            if (!response.error) {
                                this.$router.go(-1)
                            }
                        },
                    )
                },
            })
        },

        isSelectable(value, referentValue) {
            return value !== referentValue
        },

        async moveMonibox() {
            this.loadingMoveTo = true

            let cage
            await getCages({
                idres: this.residenceId,
                limit: 10000,
            }).then((response) => {
                cage = response.data.list
            })

            const formatedData = [
                {
                    name: this.residenceName,
                    value: this.residenceId,
                    type: this.residenceType,
                    selectable: this.initialData.idcag != 0,
                    children: [],
                },
            ]

            formatedData[0].children.push(...(await this.formatCageList(cage)))

            this.loadingMoveTo = false

            this.openModal("move-to", {
                title: "moveTo",
                choices: formatedData,
                valid: async (item) => {
                    // this.$emit("loading", true)
                    await updateMonibox(this.$route.params.id, {
                        building_id: Number.isInteger(item.type)
                            ? 0
                            : item.value,
                    }).then((response) => {
                        if (!response.error) {
                            switch (item.type) {
                                case "CAGE":
                                    return this.$router.push({
                                        name: "Stairwell",
                                        params: {
                                            id: item.value,
                                        },
                                    })

                                case "BATIMENT":
                                    return this.$router.push({
                                        name: "Building",
                                        params: {
                                            id: item.value,
                                        },
                                    })

                                case 0:
                                case 1:
                                case 2:
                                    return this.$router.push({
                                        name: "Residence",
                                        params: {
                                            id: item.value,
                                        },
                                    })
                            }
                        }
                    })
                },
            })
        },

        async formatCageList(oldArray) {
            let newArray = oldArray
                .filter((item) => item.pere === "0")
                .map((item) => ({
                    name: item.nom,
                    value: item.id,
                    type: item.type,
                    selectable: this.isSelectable(
                        item.id,
                        this.initialData.idcag,
                    ),
                    children: [],
                }))

            newArray.forEach((element) => {
                const children = oldArray
                    .filter((item) => item.pere === element.value)
                    .map((item) => ({
                        name: item.nom,
                        value: item.id,
                        type: item.type,
                        pere: item.pere,
                        selectable: this.isSelectable(
                            item.id,
                            this.initialData.idcag,
                        ),
                    }))

                element.children.push(...children)
            })

            return newArray
        },

        formateSerial() {
            let raw = this.serial.replace(/[^a-zA-Z0-9]/g, "")

            this.serial = raw.match(/.{1,3}/g)?.join("-") ?? ""
        },
    },

    mounted() {
        if (!this.isCreateRoute) {
            this.retrieveMonibox()
        }
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/global.scss";

.create-edit-main-container {
    display: flex;
    flex-direction: column;
    gap: 1em;
    padding-top: 1em;
    width: 100%;
    height: 100%;

    h1,
    p {
        margin: 0;
    }

    h1 {
        font-family: $font-avenir-black;
    }

    .content-container {
        width: 100%;
        display: flex;
        gap: 5em;
        height: max-content;

        .separator {
            @media all and (max-width: 768px) {
                display: none;
            }
            width: 1px;
            border-left: 1px solid $blue-neutral;
        }

        .form-container {
            width: 100%;
            max-width: 600px;
            .input-container {
                width: 100%;
                display: flex;
                flex-direction: column;
                gap: 1em;
                padding-top: 3em;

                @media all and (max-width: 768px) {
                    padding-top: 1.5em;
                }

                .input-wrapper {
                    width: 100%;
                    display: flex;
                    align-items: start;
                    gap: 1em;

                    @media all and (max-width: 768px) {
                        flex-direction: column;
                        gap: 0.5em;
                    }

                    p {
                        width: 200px;
                    }

                    :deep(.basic-input) {
                        width: 100%;
                    }
                }
            }

            .buttons-wrapper {
                padding: 2em 0;
                width: 100%;
                display: flex;
                flex-wrap: wrap;
                gap: 0.8em;

                @media all and (max-width: 768px) {
                    justify-content: center;
                }
            }
        }

        .image-wrapper {
            @media all and (max-width: 768px) {
                display: none;
            }
            img {
                width: 120px;
            }
        }
    }

    .loader {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }
}
</style>
